// ============================================================================
// CONFIG SETTING FIELD
// ============================================================================
// Renders a single config setting with the appropriate control by dataType

import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { SyutSelect } from 'components/ui';
import type { SelectOption } from 'components/ui/SyutSelect';
import ConfigListEditor from './ConfigListEditor';
import { configService } from 'api/services/configService';
import type {
  ConfigDefinition,
  ConfigEffectiveValue,
  ConfigValueUpdate,
  ConfigListItem,
} from 'api/types/config.types';
import type { ScopeType } from 'hooks/api/useConfigSettings';

interface ConfigSettingFieldProps {
  definition: ConfigDefinition;
  effectiveValue: ConfigEffectiveValue;
  editedValue?: ConfigValueUpdate;
  onValueChange: (configKey: string, value?: string, listValues?: ConfigListItem[]) => void;
  onResetValue: (configKey: string) => void;
  scopeType: ScopeType;
  /**
   * When true, the control is rendered disabled with the existing value still
   * visible. Used by the parent panel to gate dependent fields (e.g.
   * `max_extensions` / `extension_hours` when `block_extension_allowed = false`).
   * The disabled value is preserved in state so toggling the parent back ON
   * restores the previous numbers without retyping; the panel is responsible
   * for sending NULL on save when the parent toggle is OFF.
   */
  disabled?: boolean;
  /** Helper text shown under a disabled field — explains the gating reason. */
  disabledReason?: string;
}

// Keys that should render as SyutSelect dropdowns
const DROPDOWN_KEYS: Record<string, SelectOption[]> = {
  cancellation_charge_type: [
    { value: 'Percentage', label: 'Percentage' },
    { value: 'FlatRate', label: 'Flat Rate' },
    { value: 'FirstNight', label: 'First Night' },
  ],
  refund_mode: [
    { value: 'OriginalPayment', label: 'Original Payment' },
    { value: 'Credit', label: 'Credit' },
    { value: 'Both', label: 'Both' },
  ],
  pricing_model: [
    { value: 'a_la_carte', label: 'A la carte (per-pax meal billing)' },
    { value: 'bundled', label: 'Bundled (flat room+default-meal for base occupancy)' },
  ],
};

const ConfigSettingField: React.FC<ConfigSettingFieldProps> = ({
  definition,
  effectiveValue,
  editedValue,
  onValueChange,
  onResetValue,
  scopeType,
  disabled: gatedDisabled,
  disabledReason,
}) => {
  // Determine if this setting is read-only at this scope OR gated off by a
  // parent toggle. Both flags collapse into the same `isReadOnly` for the
  // control's `disabled` prop; only the visual treatment differs.
  const isSystemOnly = definition.maxScopeLevel === 0;
  const isPropertyOnly = definition.maxScopeLevel <= 1;
  const isReadOnly =
    (isSystemOnly && scopeType !== 'SYSTEM') ||
    (isPropertyOnly && scopeType === 'RATE_PLAN') ||
    !!gatedDisabled;

  // Determine badge type
  const badge = useMemo(() => {
    if (scopeType === 'SYSTEM') return null;

    if (isSystemOnly) {
      return { className: 'config-badge-system-only', label: 'System Only' };
    }
    if (scopeType === 'RATE_PLAN' && isPropertyOnly) {
      return { className: 'config-badge-system-only', label: 'Property Max' };
    }

    // Check if there's an override at the current scope level
    const isOverridden =
      effectiveValue.resolvedFromScopeType === scopeType ||
      (editedValue && editedValue.value !== undefined);
    if (isOverridden) {
      return { className: 'config-badge-override', label: 'Override' };
    }
    return { className: 'config-badge-inherited', label: 'Inherited' };
  }, [isSystemOnly, isPropertyOnly, scopeType, effectiveValue, editedValue]);

  // Current displayed value
  const currentValue = useMemo(() => {
    if (editedValue?.value !== undefined) return editedValue.value;
    return effectiveValue.effectiveValue ?? definition.defaultValue ?? '';
  }, [editedValue, effectiveValue, definition]);

  // Current list values
  const currentListValues = useMemo(() => {
    if (editedValue?.listValues) return editedValue.listValues;
    return effectiveValue.listValues ?? [];
  }, [editedValue, effectiveValue]);

  // Original list values from API — used to lock existing items from editing
  const originalListValues = useMemo(() =>
    new Set((effectiveValue.listValues ?? []).map(i => i.value)),
    [effectiveValue]);

  // Whether this field has an override at the current scope that can be reset
  const canReset =
    scopeType !== 'SYSTEM' &&
    !isReadOnly &&
    (effectiveValue.resolvedFromScopeType === scopeType ||
      editedValue !== undefined);

  const handleChange = useCallback(
    (value: string) => {
      onValueChange(definition.configKey, value);
    },
    [definition.configKey, onValueChange]
  );

  const handleListChange = useCallback(
    (listValues: ConfigListItem[]) => {
      onValueChange(definition.configKey, undefined, listValues);
    },
    [definition.configKey, onValueChange]
  );

  const handleReset = useCallback(() => {
    onResetValue(definition.configKey);
  }, [definition.configKey, onResetValue]);

  // Parse meta_data.allowed_values once per definition. Backend ships the JSON
  // as a raw string (PostgreSQL jsonb cast); shape: { "allowed_values": ["A","B"] }.
  // Used to render any String-typed setting that supplies allowed_values as a SyutSelect
  // (e.g. overdue_advance_action — AUTO_CANCEL | SEND_FOR_APPROVAL).
  const metaAllowedOptions = useMemo<SelectOption[] | null>(() => {
    if (!definition.metaData) return null;
    try {
      const parsed = JSON.parse(definition.metaData);
      const arr = parsed?.allowed_values;
      if (!Array.isArray(arr) || arr.length === 0) return null;
      return arr
        .filter((v: unknown) => typeof v === 'string' && v.length > 0)
        .map((v: string) => ({ value: v, label: v.replace(/_/g, ' ') }));
    } catch {
      return null;
    }
  }, [definition.metaData]);

  /*
   * meta_data.options_from (crs-db fixes/100, settings cleanup C11): a String setting whose value is a
   * comma-separated pick from another setting's list, e.g. fo.cash_payment_modes from
   * enabled_payment_modes. Shape: { "options_from": { "group": "PAYMENT_SETTINGS", "key": "enabled_payment_modes" } }.
   * Rendered as a multi-select so the names always match the list; stored as the same
   * comma-separated text the server reads. The options are the list's system values.
   */
  const optionsFrom = useMemo<{ group: string; key: string } | null>(() => {
    if (!definition.metaData) return null;
    try {
      const src = JSON.parse(definition.metaData)?.options_from;
      return src && typeof src.group === 'string' && typeof src.key === 'string' ? src : null;
    } catch {
      return null;
    }
  }, [definition.metaData]);

  const [sourceOptions, setSourceOptions] = useState<SelectOption[] | null>(null);
  useEffect(() => {
    if (!optionsFrom) return;
    let cancelled = false;
    configService.getEffectiveConfig(optionsFrom.group)
      .then((res) => {
        if (cancelled) return;
        const items = (res.data ?? []).find((v) => v.configKey === optionsFrom.key)?.listValues ?? [];
        setSourceOptions(items.filter((i) => i.isActive).map((i) => ({ value: i.value, label: i.value })));
      })
      .catch(() => { if (!cancelled) setSourceOptions(null); });
    return () => { cancelled = true; };
  }, [optionsFrom]);

  // Render the appropriate control
  const renderControl = () => {
    if (optionsFrom && sourceOptions) {
      const picked = currentValue.split(',').map((s) => s.trim()).filter(Boolean);
      // A saved name no longer in the list stays visible so it can be removed.
      const options = [...sourceOptions, ...picked.filter((p) => !sourceOptions.some((o) => o.value === p)).map((p) => ({ value: p, label: p }))];
      return (
        <div style={{ minWidth: '260px' }}>
          <SyutSelect
            isMulti
            value={picked}
            onChange={(value: string | string[] | null) =>
              handleChange((Array.isArray(value) ? value : value ? [value] : []).join(','))
            }
            options={options}
            isDisabled={isReadOnly}
            size="sm"
          />
        </div>
      );
    }

    // Check for hard-coded dropdown keys, then fall back to meta_data allowed_values
    const dropdownOptions = DROPDOWN_KEYS[definition.configKey] ?? metaAllowedOptions;
    if (dropdownOptions) {
      return (
        <div style={{ minWidth: '200px' }}>
          <SyutSelect
            value={currentValue}
            onChange={(value: string | string[] | null) =>
              handleChange(typeof value === 'string' ? value : '')
            }
            options={dropdownOptions}
            isDisabled={isReadOnly}
            size="sm"
          />
        </div>
      );
    }

    switch (definition.dataType) {
      case 'Boolean':
        return (
          <label className="config-toggle-switch">
            <input
              type="checkbox"
              checked={currentValue === 'true' || currentValue === 'True'}
              onChange={(e) => handleChange(e.target.checked ? 'true' : 'false')}
              disabled={isReadOnly}
            />
            <span className="config-toggle-slider" />
          </label>
        );

      case 'Integer':
        return (
          <input
            type="number"
            value={currentValue}
            onChange={(e) => handleChange(e.target.value)}
            min={definition.minValue ? parseInt(definition.minValue, 10) : undefined}
            max={definition.maxValue ? parseInt(definition.maxValue, 10) : undefined}
            step={1}
            disabled={isReadOnly}
          />
        );

      case 'Decimal':
        return (
          <input
            type="number"
            value={currentValue}
            onChange={(e) => handleChange(e.target.value)}
            min={definition.minValue ? parseFloat(definition.minValue) : undefined}
            max={definition.maxValue ? parseFloat(definition.maxValue) : undefined}
            step={0.01}
            disabled={isReadOnly}
          />
        );

      case 'List':
        return (
          <ConfigListEditor
            values={currentListValues}
            onChange={handleListChange}
            originalValues={originalListValues}
            unitLabel={definition.unitLabel}
            isReadOnly={isReadOnly}
          />
        );

      default: // String
        return (
          <input
            type="text"
            value={currentValue}
            onChange={(e) => handleChange(e.target.value)}
            disabled={isReadOnly}
          />
        );
    }
  };

  return (
    <div
      className={`config-setting-row${definition.dataType === 'List' ? ' config-setting-row--list' : ''}`}
      style={gatedDisabled ? { opacity: 0.55 } : undefined}
    >
      <div className="config-setting-info">
        <div className="config-setting-label">
          <span>{definition.displayName}</span>
          {definition.unitLabel && (
            <span className="config-setting-unit">({definition.unitLabel})</span>
          )}
          {badge && (
            <span className={`config-badge ${badge.className}`}>
              {badge.label}
            </span>
          )}
        </div>
        {definition.description && (
          <div className="config-setting-description">
            {definition.description}
          </div>
        )}
        {gatedDisabled && disabledReason && (
          <div
            className="config-setting-description"
            style={{ fontStyle: 'italic', marginTop: '0.25rem' }}
          >
            {disabledReason}
          </div>
        )}
      </div>
      <div className="config-setting-control">
        {renderControl()}
        {canReset && !gatedDisabled && (
          <button
            type="button"
            className="config-reset-btn"
            onClick={handleReset}
            title="Reset to system default"
          >
            Reset
          </button>
        )}
      </div>
    </div>
  );
};

export default ConfigSettingField;
